// Общее состояние «Поддержки»: открыто ли окно и числа для точек в меню.
//
// Меню (Sidebar) и окно (в AppShell) — соседи без общего родителя с состоянием,
// а числа нужны обоим: меню их показывает, окно после прочтения или ответа
// просит обновить. Поэтому — маленькое хранилище на модуле, как у входящих
// звонков (inboundCall/callStackSlot).

import { useSyncExternalStore } from "react";
import { api, type SupportSummary } from "@/api/client";

export type SupportMode = "user" | "admin";

const EMPTY: SupportSummary = { unread: 0, waiting: 0 };

let summary: SupportSummary = EMPTY;
let openMode: SupportMode | null = null;
// Растёт при сбросе: ответ на запрос прежнего пользователя не должен лечь в хранилище нового.
let epoch = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Числа для точек: непрочитанные ответы поддержки и (администратору) ждущие ответа. */
export function useSupportSummary(): SupportSummary {
  return useSyncExternalStore(subscribe, () => summary);
}

/** Открытое окно: обычное (мои обращения), панель администратора или null. */
export function useSupportOpen(): SupportMode | null {
  return useSyncExternalStore(subscribe, () => openMode);
}

export function openSupport(mode: SupportMode): void {
  if (openMode === mode) return;
  openMode = mode;
  emit();
}

export function closeSupport(): void {
  if (openMode === null) return;
  openMode = null;
  emit();
}

/** Принять числа от сервера. Тот же ответ не будит подписчиков зря. */
export function setSupportSummary(next: SupportSummary): void {
  if (next.unread === summary.unread && next.waiting === summary.waiting) return;
  summary = next;
  emit();
}

/** Спросить числа у сервера. Ошибка сети — точка остаётся прежней: пропавшая
 *  связь не повод гасить непрочитанное. */
export async function refreshSupportSummary(): Promise<void> {
  const mine = epoch;
  try {
    const next = await api.supportSummary();
    if (mine === epoch) setSupportSummary(next);
  } catch {
    /* следующий опрос поправит */
  }
}

/** Текущее состояние без подписки — для проверок и для кода вне React. */
export function supportSnapshot(): { summary: SupportSummary; open: SupportMode | null } {
  return { summary, open: openMode };
}

/** Вернуть хранилище в исходное состояние: при выходе и смене пользователя
 *  (иначе следующий вошедший увидит открытое окно и чужие числа), а ещё в тестах. */
export function resetSupportStore(): void {
  epoch++;
  summary = EMPTY;
  openMode = null;
  emit();
}
